import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Loader2, ShieldCheck, X } from 'lucide-react';

/**
 * GigCodeOfConductModal — the required Code of Conduct gate shown every
 * time talent accepts a gig. The Accept button is disabled until the
 * acknowledgment box is checked (no pre-check, no bypass).
 *
 * Props:
 *   open            — bool
 *   onClose()       — cancel
 *   onAgree(vid)    — called with the active version id once the talent agrees
 *   busy            — parent is submitting
 *   submitLabel     — CTA label (default "Accept Gig")
 *   version         — optional pre-loaded version (view-only / archived preview)
 *   readOnly        — hide the checkbox + Accept, just show the policy
 *   heading         — optional heading override
 */
export const GigCodeOfConductModal = ({
  open,
  onClose,
  onAgree,
  busy = false,
  submitLabel = 'Accept Gig',
  version: versionProp = null,
  readOnly = false,
  heading,
}) => {
  const [loading, setLoading] = useState(!versionProp);
  const [version, setVersion] = useState(versionProp);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { setChecked(false); setError(null); return; }
    if (versionProp) { setVersion(versionProp); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/code-of-conduct/current');
        if (!cancelled) setVersion(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load the Code of Conduct.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, versionProp]);

  if (!open) return null;

  const clauses = version?.clauses || [];

  return createPortal((
    <div
      className="fixed inset-0 z-[120] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="coc-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90dvh]" data-testid="coc-modal">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight" data-testid="coc-modal-title">
              {heading || version?.title || 'iEvntür Gig Code of Conduct'}
            </h2>
            {version?.effective_date && (
              <p className="text-xs text-slate-500 mt-0.5" data-testid="coc-modal-version">
                Effective {new Date(`${version.effective_date}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
          <button onClick={() => !busy && onClose?.()} className="text-slate-400 hover:text-slate-600 p-1" data-testid="coc-modal-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0" data-testid="coc-modal-body">
          {loading ? (
            <div className="flex items-center justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
          ) : error ? (
            <div className="text-sm text-red-600 py-6" data-testid="coc-modal-error">{typeof error === 'string' ? error : 'Could not load the Code of Conduct.'}</div>
          ) : (
            <>
              {version?.intro && (
                <p className="text-sm text-slate-600 mb-4">{version.intro}</p>
              )}
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">By accepting this gig, you agree to:</p>
              <ol className="space-y-2.5" data-testid="coc-clause-list">
                {clauses.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-700" data-testid={`coc-clause-${i}`}>
                    <span className="shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="leading-snug">{c}</span>
                  </li>
                ))}
              </ol>
              {version?.footer && (
                <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-100 leading-relaxed" data-testid="coc-footer">{version.footer}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!readOnly && !loading && !error && (
          <div className="px-5 py-4 border-t border-slate-100 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="coc-ack-label">
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => setChecked(!!v)}
                className="mt-0.5"
                data-testid="coc-ack-checkbox"
              />
              <span className="text-sm text-slate-700 leading-snug">{version?.checkbox_text}</span>
            </label>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => onClose?.()} disabled={busy} data-testid="coc-cancel-btn">
                Cancel
              </Button>
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                disabled={!checked || busy}
                onClick={() => onAgree?.(version?.id)}
                data-testid="coc-accept-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {submitLabel}
              </Button>
            </div>
          </div>
        )}
        {readOnly && !loading && (
          <div className="px-5 py-3 border-t border-slate-100">
            <Button variant="outline" className="w-full" onClick={() => onClose?.()} data-testid="coc-close-readonly-btn">Close</Button>
          </div>
        )}
      </div>
    </div>
  ), document.body);
};

export default GigCodeOfConductModal;
